import styled from "styled-components"

import { colors } from "@minuthq/meatball-ui-react"
import { BodyMixin, Text } from "@minuthq/meatball-ui-react"

import { HREF_MINUT_HELP_SENSORS_OFFLINE } from "src/constants/hrefs"
import { useTranslate } from "src/i18n/useTranslate"
import { ExpandableAttentionCard } from "src/ui/ExpandableSection/ExpandableAttentionCard"
import AlertThreeBuildings from "src/ui/icons/illustrations/three-buildings-color-error.svg"
import { ExternalLink } from "src/ui/Link/ExternalLink"
import { spacing } from "src/ui/spacing"

const wideWidth = "500px"

export function NonLiveBanner({
  title,
  description,
  alertBullets,
  isEmergency,
}: {
  title: string
  description: string
  alertBullets: { icon: React.ReactNode; title: string }[]
  isEmergency?: boolean
}) {
  const { t, langKeys } = useTranslate()

  return (
    <ExpandableAttentionCard
      type="emergency"
      initialOpen={true}
      title={
        <Text variant="subtitle" color="contrast">
          {title}
        </Text>
      }
      summaryProps={{
        bgColor: isEmergency ? colors.systemError : colors.systemWarning,
        fgColor: colors.textContrast,
      }}
    >
      <ContainerBox>
        <DescriptionBox>
          <AlertDescription>
            <StyledBuildings />
            <div>
              {description}{" "}
              <ExternalLink href={HREF_MINUT_HELP_SENSORS_OFFLINE}>
                {t(langKeys.read_more)}
              </ExternalLink>
            </div>
          </AlertDescription>

          <NoAlertBox>
            <div>{t(langKeys.home_unmonitored_no_alerts_title)}</div>
            {alertBullets.map((bullet) => (
              <IconTextBox key={bullet.title}>
                {bullet.icon}
                <div>{bullet.title}</div>
              </IconTextBox>
            ))}
          </NoAlertBox>
        </DescriptionBox>
      </ContainerBox>
    </ExpandableAttentionCard>
  )
}

const DescriptionBox = styled.div`
  ${BodyMixin};
  display: grid;
  grid-template-columns: 1fr;
  width: auto;
  column-gap: ${spacing.XL3};
  row-gap: ${spacing.L};
  align-items: center;

  @container (width > ${wideWidth}) {
    grid-template-columns: 1fr 1fr;
  }
`

const AlertDescription = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.L};
  border-bottom: 1px solid ${colors.divider};
  align-items: center;
  justify-content: center;

  @container (width > ${wideWidth}) {
    border-right: 1px solid ${colors.divider};
    padding-right: ${spacing.L};
    border-bottom: unset;
  }

  & > * {
    flex: 1 0 150px;
  }
`

const StyledBuildings = styled(AlertThreeBuildings)`
  width: 100%;
  max-width: 130px;
`

const IconTextBox = styled.div`
  display: flex;
  gap: ${spacing.S};
  align-items: center;
`

const NoAlertBox = styled.div`
  display: grid;
  gap: ${spacing.S};
  align-content: start;
`

const ContainerBox = styled.div`
  container-type: inline-size;
`
